import React, { FC, useState, useRef, useContext, useEffect } from 'react';
import cx from 'classnames';
import { v1 as uuid } from 'uuid';
import { RegisterOptions, useForm, Controller } from 'react-hook-form';
import HCaptcha from '@hcaptcha/react-hcaptcha';

import { LocaleContext } from '@/contexts/locale-context';
import useThemeBlock from '@/hooks/useThemeBlock';
import {
	uploadFile,
	getAcceptValidator,
	getMaxFilesizeValidator,
} from '@/helpers/Form/input-file-helpers';
import {
	FormSectionBreaker,
	InputEmail,
	InputFile,
	InputSelect,
	InputText,
	InputTextarea,
	InputYesNo,
	ButtonPrimary,
} from '@/components/atoms/renders';

import { getStyles } from './styles.css';
import block from './block.json';

/**
 * TYPINGS
 */
type FormProps = ThemeProps & {
	id: string; // id of the form in the backend
	strings?: {
		[id: string]: string;
	};
	fields: Array<any>;
};

/**
 * COMPONENT
 */
export const Form: FC<FormProps> & BlockConfigs = ({
	id,
	strings: { submitLabel = 'Send' } = {},
	fields,
	theme = {},
}) => {
	const {
		register,
		handleSubmit,
		errors,
		formState,
		setError,
		clearErrors,
		control,
	} = useForm({ mode: 'onSubmit', shouldFocusError: true });

	const { __t } = useContext(LocaleContext);

	const [successMessage, setSuccessMessage] = useState<string>();
	const [token, setToken] = useState(null); // hCaptcha token
	const [data, setData] = useState(null); // Form Data

	const captchaRef = useRef(null);
	const defaultErrorMessage = useRef(
		__t(
			'form-error-message',
			"There was an error and your message probably didn't get to us, sorry. Please try later or contact us directly by e-mail"
		)
	);

	const { variant, blockTheme } = useThemeBlock(block.slug, theme);

	const htmlProps = {
		'data-variant': variant,
	};

	const rootClass = cx('supt-form', getStyles(blockTheme));

	// Triggered once hCaptcha token changed (means it's been validated/verified)
	useEffect(() => {
		if (token) {
			submitForm();
		}
	}, [token]);

	// Triggered once the form is submitted
	const onSubmit = async (data, e) => {
		e?.preventDefault();

		setData(data); // Set form data to be able to use it afterwards (once captcha is validated)

		// validate hCaptcha before sending the form to avoid spams
		try {
			await captchaRef.current.execute();
		} catch (e) {
			return setError('__global', {
				message: defaultErrorMessage.current,
			});
		}
	};

	// Submit form data to our server
	const submitForm = async () => {
		const formData = new FormData();
		formData.append('id', id); // formID

		formData.append('_tk', token); // hCaptcha token

		const uploadId = uuid();
		const uploadPromises = Object.keys(data).reduce((promises, key) => {
			if (typeof data[key] === 'object') {
				// FileList data type
				[...data[key]].forEach((file) =>
					promises.push(uploadFile(file, key, uploadId))
				);
			}
			return promises;
		}, []);
		if (uploadPromises.length) formData.append('uploadId', uploadId);

		const uploadResults: Array<any> =
			(await Promise.all(uploadPromises).catch((e) => {
				/* console.log('Error', e) */
			})) || [];
		uploadResults.forEach((file, i) =>
			formData.append(`${file.key}[]`, file.name)
		);

		Object.keys(data).forEach((key) => {
			// Files have already been uploaded & appended
			if (typeof data[key] !== 'object') {
				formData.append(key, data[key]);
			}
		});

		// send form
		const res: any = await fetch(`/api/submit-form/`, {
			method: 'POST',
			body: formData,
		}).catch((e) => {});

		const json: any = await res.json().catch((e) => {});

		// unhandled error
		if (res.status !== 200 && !json?.data?.errors) {
			return setError('__global', {
				message: defaultErrorMessage.current,
			});
		}

		// handled error(s)
		if (json?.data?.errors) {
			Object.keys(json.data.errors)?.forEach((name) => {
				const errs = json.data.errors[name];
				setError(name, {
					message: Array.isArray(errs) ? errs.join('<br>') : errs,
				});
			});
			return;
		}

		// success!
		setSuccessMessage(json?.data?.message);
		return;
	};

	return (
		<form
			className={rootClass}
			data-block={block.slug}
			{...htmlProps}
			onSubmit={handleSubmit(onSubmit)}
			onChange={() => !!errors['__global'] && clearErrors('__global')}
			noValidate
		>
			<div className="supt-form__fields">
				{fields && fields.length
					? fields.map(({ block, attributes }, i) => {
							const attrs = {
								...attributes,
								inputAttributes: {
									disabled:
										formState.isSubmitting ||
										formState.isSubmitSuccessful,
								},
								invalid: errors?.[attributes.name]
									? errors?.[attributes.name]?.message
									: false,
							};

							const registerAttrs: RegisterOptions = {
								required: {
									value: attributes.required,
									message: __t(
										'form-input-error-empty',
										'Please fill out this field.'
									),
								},
							};

							if (block === 'supt/form-section-breaker') {
								return (
									<FormSectionBreaker
										key={i}
										{...attributes}
									/>
								);
							}

							if (block === 'supt/input-text') {
								return (
									<InputText
										key={i}
										{...attrs}
										ref={register(registerAttrs)}
									/>
								);
							}
							if (block === 'supt/input-textarea') {
								return (
									<InputTextarea
										key={i}
										{...attrs}
										ref={register(registerAttrs)}
									/>
								);
							}
							if (block === 'supt/input-select') {
								return (
									<Controller
										key={i}
										name={attrs.name}
										control={control}
										rules={registerAttrs}
										defaultValue={''}
										render={({
											onChange,
											onBlur,
											value,
											ref,
											name,
										}) => {
											return (
												<InputSelect
													{...attrs}
													onBlur={onBlur}
													name={name}
													ref={ref}
													value={value}
													onChange={onChange}
												/>
											);
										}}
									/>
								);
							}
							if (block === 'supt/input-file') {
								registerAttrs.validate = {};
								if (
									attrs.maxFilesize &&
									attrs.maxFilesize > 0
								) {
									registerAttrs.validate.maxFilesize =
										getMaxFilesizeValidator(
											attrs.maxFilesize,
											__t(
												'form-input-error-filesize',
												'Your file size is larger than %dMB.'
											)
										);
								}

								if (attrs.accept && attrs.accept !== '') {
									registerAttrs.validate.accept =
										getAcceptValidator(
											attrs.accept,
											__t(
												'form-input-error-accept',
												'Your file is invalid. Allowed files are %s.'
											)
										);
								}
								return (
									<InputFile
										key={i}
										{...attrs}
										ref={register(registerAttrs)}
									/>
								);
							}
							if (block === 'supt/input-email') {
								// add react-hook-form validation constraint
								registerAttrs.pattern = {
									value: /\S+@\S+\.\S+/,
									message: __t(
										'form-input-error-email',
										'Please enter a valid email address.'
									),
								};
								return (
									<InputEmail
										key={i}
										{...attrs}
										ref={register(registerAttrs)}
									/>
								);
							}
							if (block === 'supt/input-yesno') {
								return (
									<InputYesNo
										key={i}
										{...attrs}
										ref={register(registerAttrs)}
									/>
								);
							}
					  })
					: null}
				{process.env.NEXT_PUBLIC_HCAPTCHA_KEY ? (
					<HCaptcha
						sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_KEY}
						ref={captchaRef}
						size="invisible"
						onVerify={setToken}
					/>
				) : null}
			</div>
			<div className="supt-form__actions">
				<ButtonPrimary
					type="submit"
					className={cx('supt-form__submit', {
						isSubmitting: formState.isSubmitting,
						isSuccess: formState.isSubmitSuccessful,
					})}
					title={
						formState.isSubmitSuccessful
							? __t('form-status-success', 'Sent')
							: formState.isSubmitting
							? __t('form-status-pending', 'Sending…')
							: submitLabel || __t('form-submit-label', 'Submit')
					}
					disabled={
						formState.isSubmitting ||
						formState.isSubmitSuccessful ||
						!!Object.keys(errors).length
					}
				/>
			</div>
			<div className="supt-form__messages">
				{formState.isSubmitted &&
				(errors?.['__global']?.message ||
					!!Object.keys(errors).length) ? (
					<div role="alert" className="supt-form__messages__error">
						<p>
							{errors?.['__global']?.message ||
								(!!Object.keys(errors).length
									? __t(
											'form-error-empty',
											'Please fill out the form entirely to be able to send it.'
									  )
									: '')}
						</p>
					</div>
				) : null}
				{formState.isSubmitSuccessful && successMessage ? (
					<div role="status" className="supt-form__messages__success">
						<p>{successMessage}</p>
					</div>
				) : null}
			</div>
			{/* <pre>
				<code>{JSON.stringify(formState, null, 4)}</code>
			</pre> */}
		</form>
	);
};

Form.slug = block.slug;
Form.title = block.title;
